/**
 * Notifications frontend helpers — Phase G3C-B.
 *
 * Shared metadata for type-to-icon mapping, relative-time formatting,
 * and action_url resolution. Kept in a plain JS module so both the
 * bell dropdown and the full-history route consume one source of
 * truth (no visual drift between surfaces).
 *
 * Voice discipline:
 *   - Chip labels are neutral nouns ("Deadline", "Document gap")
 *   - Empty-state copy is defined here so it renders identically
 *     across surfaces
 */
import {
  AlertCircle,
  FileWarning,
  Repeat,
  Edit3,
  Star,
  Bell,
} from "lucide-react";

// Type → { label, icon, tint } — icon lookup is component-level (JSX)
// so we ship the lucide component reference here rather than a string.
export const TYPE_META = {
  grant_deadline_approaching: {
    label: "Deadline",
    icon: AlertCircle,
    tint: "rgb(167,139,250)", // v2 accent
    tintBg: "rgba(167,139,250,0.14)",
    tintBorder: "rgba(167,139,250,0.42)",
  },
  document_gap: {
    label: "Document gap",
    icon: FileWarning,
    tint: "rgb(251,191,36)", // amber
    tintBg: "rgba(251,191,36,0.12)",
    tintBorder: "rgba(251,191,36,0.42)",
  },
  grant_status_changed: {
    label: "Status update",
    icon: Repeat,
    tint: "rgb(56,189,248)", // sky
    tintBg: "rgba(56,189,248,0.12)",
    tintBorder: "rgba(56,189,248,0.42)",
  },
  saved_grant_updated: {
    label: "Grant edited",
    icon: Edit3,
    tint: "rgb(148,163,184)", // slate
    tintBg: "rgba(148,163,184,0.10)",
    tintBorder: "rgba(148,163,184,0.42)",
  },
  new_matching_grant: {
    label: "New match",
    icon: Star,
    tint: "rgb(52,211,153)", // emerald
    tintBg: "rgba(52,211,153,0.12)",
    tintBorder: "rgba(52,211,153,0.42)",
  },
};

// Fallback if a future notification_type slips in ahead of a UI update.
export const FALLBACK_TYPE_META = {
  label: "Notification",
  icon: Bell,
  tint: "rgb(148,163,184)",
  tintBg: "rgba(148,163,184,0.10)",
  tintBorder: "rgba(148,163,184,0.42)",
};

export function metaFor(type) {
  return TYPE_META[type] || FALLBACK_TYPE_META;
}

// "in 12 days" / "3 days ago" / "just now" — short + local. Used on
// dropdown rows AND full-history rows so we render one relative
// string across the app.
export function formatRelative(iso, now = new Date()) {
  if (!iso) return "";
  const target = new Date(iso);
  const diffMs = target.getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  const past = diffMs < 0;

  const min = 60 * 1000;
  const hr = 60 * min;
  const day = 24 * hr;

  if (absMs < 60 * 1000) return "just now";
  if (absMs < hr) {
    const m = Math.round(absMs / min);
    return past ? `${m}m ago` : `in ${m}m`;
  }
  if (absMs < day) {
    const h = Math.round(absMs / hr);
    return past ? `${h}h ago` : `in ${h}h`;
  }
  const d = Math.round(absMs / day);
  if (past) {
    if (d === 1) return "yesterday";
    return `${d} days ago`;
  }
  if (d === 1) return "tomorrow";
  return `in ${d} days`;
}

// Bell-badge display cap — per user CP2 brief: "99+" once we exceed 99.
export function formatBadgeCount(n) {
  if (!n || n < 1) return "";
  if (n > 99) return "99+";
  return String(n);
}

// Empty-state copy — verbatim per user CP2 brief. Duplicated here so
// both the dropdown + full-history route render identically.
export const EMPTY_STATE_COPY =
  "No notifications yet. You'll see deadline reminders, status updates, and new matching grants here.";
